import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../components/ui/dialog';
import { toast } from 'sonner';
import { CreditCard, ArrowUpCircle, ArrowDownCircle, XCircle, RefreshCw, Users, Check, AlertTriangle, Clock, Shield, Loader2 } from 'lucide-react';
import api from '../lib/api';

const formatPrice = (v) => v != null ? Number(v).toFixed(2).replace('.', ',') : '—';
const planOrder = { basic: 0, pro: 1, premium: 2 };

export default function BillingPage() {
  const [sub, setSub] = useState(null);
  const [loading, setLoading] = useState(true);
  const [members, setMembers] = useState([]);
  const [billingInterval, setBillingInterval] = useState('monthly');

  // Dialog states
  const [cancelDialog, setCancelDialog] = useState(false);
  const [changeDialog, setChangeDialog] = useState(null); // plan object or null
  const [actionLoading, setActionLoading] = useState(false);

  const load = useCallback(async () => {
    try {
      const [subRes, membersRes] = await Promise.all([
        api.get('/billing/subscription'),
        api.get('/team/members'),
      ]);
      setSub(subRes.data);
      setMembers(membersRes.data.members || []);
      if (subRes.data.interval) setBillingInterval(subRes.data.interval === 'year' ? 'yearly' : 'monthly');
    } catch (e) {
      toast.error('Fehler beim Laden der Abo-Daten');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleCancel = async () => {
    setActionLoading(true);
    try {
      await api.post('/billing/cancel');
      toast.success('Abo zum Periodenende gekündigt');
      setCancelDialog(false);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Fehler beim Kündigen');
    } finally { setActionLoading(false); }
  };

  const handleReactivate = async () => {
    setActionLoading(true);
    try {
      await api.post('/billing/reactivate');
      toast.success('Kündigung zurückgenommen');
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Fehler beim Reaktivieren');
    } finally { setActionLoading(false); }
  };

  const handleChangePlan = async (planId) => {
    setActionLoading(true);
    try {
      const res = await api.post('/billing/change-plan', { plan_id: planId });
      toast.success(res.data.message);
      setChangeDialog(null);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Fehler beim Planwechsel');
    } finally { setActionLoading(false); }
  };

  const handleDeactivateMember = async (memberId) => {
    try {
      await api.post(`/team/members/${memberId}/deactivate`);
      toast.success('Mitglied deaktiviert');
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Fehler');
    }
  };

  const handleReactivateMember = async (memberId) => {
    try {
      await api.post(`/team/members/${memberId}/reactivate`);
      toast.success('Mitglied reaktiviert');
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Fehler');
    }
  };

  if (loading) return <div className="flex items-center justify-center h-64"><Loader2 className="w-8 h-8 animate-spin text-muted-foreground" /></div>;
  if (!sub) return <div className="p-6 text-center text-muted-foreground">Keine Abo-Daten verfügbar</div>;

  const currentPlan = sub.plan_details;
  const isActive = ['active', 'canceling'].includes(sub.subscription_status);
  const isCanceling = sub.cancel_at_period_end;
  const hasSubscription = sub.subscription_plan && isActive;

  return (
    <div className="max-w-5xl mx-auto p-6 space-y-6" data-testid="billing-page">
      {/* Current Plan Overview */}
      <Card data-testid="current-plan-card">
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="flex items-center gap-2">
                <CreditCard className="w-5 h-5" />
                Ihr Abonnement
              </CardTitle>
              <CardDescription className="mt-1">
                {currentPlan ? `${currentPlan.name}-Plan` : 'Kein aktiver Plan'} {sub.is_demo && <Badge variant="outline" className="ml-2">Demo</Badge>}
              </CardDescription>
            </div>
            <Badge className={isCanceling ? 'bg-amber-100 text-amber-700 border-amber-200' : isActive ? 'bg-emerald-100 text-emerald-700 border-emerald-200' : 'bg-red-100 text-red-700 border-red-200'} data-testid="subscription-status-badge">
              {isCanceling ? 'Wird gekündigt' : sub.subscription_status === 'active' ? 'Aktiv' : sub.subscription_status === 'past_due' ? 'Zahlung ausstehend' : 'Inaktiv'}
            </Badge>
          </div>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
            <div className="p-3 rounded-lg bg-muted/50">
              <p className="text-xs text-muted-foreground">Preis</p>
              <p className="text-lg font-semibold" data-testid="current-price">
                {currentPlan ? `${formatPrice(billingInterval === 'yearly' ? currentPlan.yearly_eur / 12 : currentPlan.monthly_eur)}€/Mo` : '—'}
              </p>
            </div>
            <div className="p-3 rounded-lg bg-muted/50">
              <p className="text-xs text-muted-foreground">Team</p>
              <p className="text-lg font-semibold" data-testid="member-count">{sub.member_count || 0} / {sub.max_users || '—'} Nutzer</p>
            </div>
            <div className="p-3 rounded-lg bg-muted/50">
              <p className="text-xs text-muted-foreground">Leads / Monat</p>
              <p className="text-lg font-semibold">{currentPlan?.max_leads_per_month || '—'}</p>
            </div>
            <div className="p-3 rounded-lg bg-muted/50">
              <p className="text-xs text-muted-foreground">Abrechnungszeitraum</p>
              <p className="text-lg font-semibold">{billingInterval === 'yearly' ? 'Jährlich' : 'Monatlich'}</p>
            </div>
          </div>

          {isCanceling && sub.current_period_end && (
            <div className="flex items-center gap-3 p-3 rounded-lg bg-amber-50 border border-amber-200 mb-4" data-testid="canceling-notice">
              <AlertTriangle className="w-5 h-5 text-amber-600 shrink-0" />
              <div className="flex-1">
                <p className="text-sm font-medium text-amber-800">Ihr Abo endet am {new Date(sub.current_period_end).toLocaleDateString('de-DE')}</p>
                <p className="text-xs text-amber-600">Bis dahin haben Sie weiterhin vollen Zugriff auf alle Funktionen.</p>
              </div>
              <Button size="sm" variant="outline" onClick={handleReactivate} disabled={actionLoading} data-testid="reactivate-btn">
                {actionLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4 mr-1" />}
                Kündigung zurücknehmen
              </Button>
            </div>
          )}

          {!hasSubscription && !sub.is_demo && (
            <div className="flex items-center gap-3 p-3 rounded-lg bg-blue-50 border border-blue-200 mb-4" data-testid="no-subscription-notice">
              <CreditCard className="w-5 h-5 text-blue-600 shrink-0" />
              <div className="flex-1">
                <p className="text-sm font-medium text-blue-800">Kein aktives Abonnement</p>
                <p className="text-xs text-blue-600">Wählen Sie einen Plan aus der untenstehenden Liste, um loszulegen.</p>
              </div>
            </div>
          )}

          {isActive && !isCanceling && (
            <div className="flex gap-3">
              <Button variant="destructive" size="sm" onClick={() => setCancelDialog(true)} data-testid="cancel-btn">
                <XCircle className="w-4 h-4 mr-1" /> Abo kündigen
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Plan Comparison / Change */}
      <Card data-testid="plan-change-card">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <ArrowUpCircle className="w-5 h-5" />
            {hasSubscription ? 'Plan wechseln' : 'Plan auswählen'}
          </CardTitle>
          <CardDescription>Wählen Sie den Plan, der am besten zu Ihrem Team passt</CardDescription>
        </CardHeader>
        <CardContent>
          {/* Billing interval toggle */}
          <div className="flex items-center justify-center gap-3 mb-6">
            <span className={`text-sm font-medium transition-colors ${billingInterval === 'monthly' ? 'text-foreground' : 'text-muted-foreground'}`}>Monatlich</span>
            <button
              onClick={() => setBillingInterval(i => i === 'monthly' ? 'yearly' : 'monthly')}
              className={`relative inline-flex h-7 w-12 items-center rounded-full transition-colors ${billingInterval === 'yearly' ? 'bg-emerald-600' : 'bg-muted'}`}
              data-testid="billing-interval-toggle"
            >
              <span className={`inline-block h-5 w-5 transform rounded-full bg-white shadow-sm transition-transform ${billingInterval === 'yearly' ? 'translate-x-6' : 'translate-x-1'}`} />
            </button>
            <span className={`text-sm font-medium transition-colors ${billingInterval === 'yearly' ? 'text-foreground' : 'text-muted-foreground'}`}>Jährlich</span>
            {billingInterval === 'yearly' && <Badge className="bg-emerald-100 text-emerald-700 border-emerald-200 text-xs">Spare bis zu 10%</Badge>}
          </div>
          <div className="grid md:grid-cols-3 gap-4">
            {(sub.available_plans || []).map(plan => {
              const isCurrent = hasSubscription && plan.id === sub.subscription_plan;
              const isUpgrade = hasSubscription && planOrder[plan.id] > planOrder[sub.subscription_plan];
              const isDowngrade = hasSubscription && planOrder[plan.id] < planOrder[sub.subscription_plan];
              const price = billingInterval === 'yearly' ? plan.yearly_eur / 12 : plan.monthly_eur;
              const tooManyUsers = hasSubscription && plan.max_users < sub.member_count;

              return (
                <div key={plan.id} className={`p-4 rounded-xl border-2 transition-colors ${isCurrent ? 'border-primary bg-primary/5' : 'border-border hover:border-primary/30'}`} data-testid={`plan-option-${plan.id}`}>
                  <div className="flex items-center justify-between mb-3">
                    <h3 className="font-semibold text-lg">{plan.name}</h3>
                    {isCurrent && <Badge className="bg-primary text-primary-foreground">Aktuell</Badge>}
                    {plan.badge_text && !isCurrent && <Badge variant="outline">{plan.badge_text}</Badge>}
                  </div>
                  <p className="text-2xl font-bold mb-1">{formatPrice(price)}€<span className="text-sm font-normal text-muted-foreground">/Mo</span></p>
                  {billingInterval === 'yearly' && (
                    <p className="text-xs text-muted-foreground mb-1">Gesamt: {formatPrice(plan.yearly_eur)}€/Jahr</p>
                  )}
                  <ul className="space-y-1.5 my-3 text-sm">
                    <li className="flex items-center gap-2"><Users className="w-3.5 h-3.5 text-muted-foreground" /> {plan.max_users} Nutzer</li>
                    <li className="flex items-center gap-2"><Check className="w-3.5 h-3.5 text-emerald-500" /> {plan.max_leads_per_month} Leads/Monat</li>
                  </ul>
                  {!isCurrent && hasSubscription && (
                    <Button className="w-full" variant={isUpgrade ? 'default' : 'outline'} size="sm"
                      disabled={tooManyUsers}
                      onClick={() => setChangeDialog(plan)}
                      data-testid={`change-to-${plan.id}-btn`}>
                      {tooManyUsers ? (
                        <span className="flex items-center gap-1"><AlertTriangle className="w-3.5 h-3.5" /> Zu viele Nutzer ({sub.member_count}/{plan.max_users})</span>
                      ) : isUpgrade ? (
                        <span className="flex items-center gap-1"><ArrowUpCircle className="w-3.5 h-3.5" /> Upgrade</span>
                      ) : (
                        <span className="flex items-center gap-1"><ArrowDownCircle className="w-3.5 h-3.5" /> Downgrade</span>
                      )}
                    </Button>
                  )}
                  {!hasSubscription && (
                    <Button className="w-full" size="sm"
                      onClick={() => window.location.href = '/onboarding'}
                      data-testid={`select-plan-${plan.id}-btn`}>
                      Plan auswählen
                    </Button>
                  )}
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>

      {/* Team Members */}
      <Card data-testid="team-members-card">
        <CardHeader>
          <CardTitle className="flex items-center gap-2"><Users className="w-5 h-5" /> Team-Mitglieder ({sub.member_count}/{sub.max_users})</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-2">
            {members.map(m => (
              <div key={m.id} className="flex items-center justify-between p-3 rounded-lg border border-border/50" data-testid={`member-${m.id}`}>
                <div className="flex items-center gap-3">
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-semibold ${m.is_active === false ? 'bg-red-100 text-red-600' : 'bg-primary/10 text-primary'}`}>
                    {(m.name || m.email || '?')[0].toUpperCase()}
                  </div>
                  <div>
                    <p className="text-sm font-medium">{m.name || m.email}</p>
                    <p className="text-xs text-muted-foreground">{m.email} {m.role === 'owner' && <Badge variant="outline" className="ml-1 text-[10px] py-0">Inhaber</Badge>}</p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  {m.is_active === false && <Badge variant="outline" className="border-red-200 text-red-600 text-xs">Deaktiviert</Badge>}
                  {m.role !== 'owner' && (
                    m.is_active === false ? (
                      <Button size="sm" variant="outline" onClick={() => handleReactivateMember(m.id)} data-testid={`reactivate-member-${m.id}`}>Reaktivieren</Button>
                    ) : (
                      <Button size="sm" variant="ghost" className="text-red-600 hover:text-red-700 hover:bg-red-50" onClick={() => handleDeactivateMember(m.id)} data-testid={`deactivate-member-${m.id}`}>Deaktivieren</Button>
                    )
                  )}
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Cancel Dialog */}
      <Dialog open={cancelDialog} onOpenChange={setCancelDialog}>
        <DialogContent data-testid="cancel-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><AlertTriangle className="w-5 h-5 text-red-500" /> Abo kündigen</DialogTitle>
            <DialogDescription>
              Sind Sie sicher, dass Sie Ihr Abonnement kündigen möchten?
            </DialogDescription>
          </DialogHeader>
          <div className="p-4 rounded-lg bg-amber-50 border border-amber-200 space-y-2">
            <div className="flex items-start gap-2">
              <Clock className="w-4 h-4 text-amber-600 mt-0.5 shrink-0" />
              <p className="text-sm text-amber-800">Ihr Abo bleibt bis zum Ende des aktuellen Abrechnungszeitraums aktiv{sub.current_period_end ? ` (${new Date(sub.current_period_end).toLocaleDateString('de-DE')})` : ''}.</p>
            </div>
            <div className="flex items-start gap-2">
              <Shield className="w-4 h-4 text-amber-600 mt-0.5 shrink-0" />
              <p className="text-sm text-amber-800">Alle Ihre Daten und Leads bleiben erhalten. Sie können das Abo jederzeit reaktivieren.</p>
            </div>
            <div className="flex items-start gap-2">
              <Users className="w-4 h-4 text-amber-600 mt-0.5 shrink-0" />
              <p className="text-sm text-amber-800">Nach Ablauf verlieren alle Team-Mitglieder den Zugang.</p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCancelDialog(false)}>Abbrechen</Button>
            <Button variant="destructive" onClick={handleCancel} disabled={actionLoading} data-testid="confirm-cancel-btn">
              {actionLoading ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <XCircle className="w-4 h-4 mr-1" />}
              Abo kündigen
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Change Plan Dialog */}
      <Dialog open={!!changeDialog} onOpenChange={() => setChangeDialog(null)}>
        <DialogContent data-testid="change-plan-dialog">
          <DialogHeader>
            <DialogTitle>
              {changeDialog && planOrder[changeDialog.id] > planOrder[sub.subscription_plan] ? 'Plan upgraden' : 'Plan downgraden'}
            </DialogTitle>
            <DialogDescription>
              {changeDialog && planOrder[changeDialog.id] > planOrder[sub.subscription_plan]
                ? `Wechsel von ${currentPlan?.name || '—'} zu ${changeDialog?.name}. Die Differenz wird anteilig berechnet.`
                : `Wechsel von ${currentPlan?.name || '—'} zu ${changeDialog?.name}. Der neue Plan wird zum Ende des Abrechnungszeitraums aktiv.`
              }
            </DialogDescription>
          </DialogHeader>
          {changeDialog && (
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div className="p-3 rounded-lg border">
                  <p className="text-xs text-muted-foreground mb-1">Aktuell: {currentPlan?.name}</p>
                  <p className="font-semibold">{formatPrice(billingInterval === 'yearly' ? (currentPlan?.yearly_eur || 0) / 12 : currentPlan?.monthly_eur)}€/Mo</p>
                  <p className="text-xs text-muted-foreground">{currentPlan?.max_users} Nutzer · {currentPlan?.max_leads_per_month} Leads</p>
                </div>
                <div className="p-3 rounded-lg border border-primary bg-primary/5">
                  <p className="text-xs text-muted-foreground mb-1">Neu: {changeDialog.name}</p>
                  <p className="font-semibold">{formatPrice(billingInterval === 'yearly' ? changeDialog.yearly_eur / 12 : changeDialog.monthly_eur)}€/Mo</p>
                  <p className="text-xs text-muted-foreground">{changeDialog.max_users} Nutzer · {changeDialog.max_leads_per_month} Leads</p>
                </div>
              </div>
              {changeDialog.max_users < sub.member_count && (
                <div className="p-3 rounded-lg bg-red-50 border border-red-200">
                  <p className="text-sm text-red-800 flex items-center gap-2">
                    <AlertTriangle className="w-4 h-4 shrink-0" />
                    Sie haben {sub.member_count} aktive Nutzer, der {changeDialog.name}-Plan erlaubt nur {changeDialog.max_users}. Bitte deaktivieren Sie zuerst {sub.member_count - changeDialog.max_users} Mitglied(er).
                  </p>
                </div>
              )}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setChangeDialog(null)}>Abbrechen</Button>
            <Button onClick={() => handleChangePlan(changeDialog?.id)} disabled={actionLoading || (changeDialog && changeDialog.max_users < sub.member_count)} data-testid="confirm-change-plan-btn">
              {actionLoading ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : null}
              Plan wechseln
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
